import { GRANTED_DOCUMENT_ACCESS_LEVELS_HIGHEST_FIRST } from '@omnea-digital/authz-model'
import type {
  DocumentAccessLevel,
  GrantedDocumentAccessLevel,
  PermissionOf,
  PermissionSetEntity,
  PermissionSetPermission,
} from '@omnea-digital/authz-model'

// One toggle per level: the model's ladder (manage confers edit, edit confers view) supplies the rest.
const DOCUMENT_ACCESS_PERMISSION = {
  request: { view: 'view_documents', edit: 'edit_documents', full: 'manage_documents' },
  supplier: { view: 'view_related_documents', edit: 'edit_documents', full: 'manage_documents' },
  agreement: { view: 'view_documents', edit: 'edit_documents', full: 'manage_documents' },
} as const satisfies {
  [Entity in 'request' | 'supplier' | 'agreement']: Record<GrantedDocumentAccessLevel, PermissionOf<Entity>>
}

export type DocumentEntity = keyof typeof DOCUMENT_ACCESS_PERMISSION

export const isDocumentEntity = (entity: PermissionSetEntity): entity is DocumentEntity => entity in DOCUMENT_ACCESS_PERMISSION

export const documentAccessPermissions = (entity: DocumentEntity): readonly PermissionSetPermission[] =>
  Object.values(DOCUMENT_ACCESS_PERMISSION[entity])

/** The highest level the permissions hold, so a set saved before the ladder still reads as one level. */
export const documentAccessLevelOf = (
  entity: DocumentEntity,
  permissions: readonly PermissionSetPermission[]
): DocumentAccessLevel =>
  GRANTED_DOCUMENT_ACCESS_LEVELS_HIGHEST_FIRST.find((level) => permissions.includes(DOCUMENT_ACCESS_PERMISSION[entity][level])) ??
  'none'

export const withDocumentAccessLevel = (
  entity: DocumentEntity,
  permissions: readonly PermissionSetPermission[],
  level: DocumentAccessLevel
): PermissionSetPermission[] => {
  const toggles = documentAccessPermissions(entity)
  const others = permissions.filter((permission) => !toggles.includes(permission))
  return level === 'none' ? others : [...others, DOCUMENT_ACCESS_PERMISSION[entity][level]]
}
